import {
  flexRender,
  type CellContext,
  type ColumnDef,
  type Row,
  type RowData,
} from '@tanstack/react-table';
import type { ReactNode } from 'react';

import { cn } from '@/shared/helpers';

import { getColumnDefId } from '../../get-column-def-id';
import type { EditableTableSchema } from '../types';
import {
  EditableCellDisplay,
  type EditableCellCommitResult,
  type EditableCellCommitArgs,
  type EditableCellEditRequestArgs,
} from './editable-cell-display';
import { RenderFromDraftCell } from './render-from-draft-cell';

export type WrapEditableColumnsArgs<TData extends RowData> = {
  columns: ColumnDef<TData, any>[];
  schema: EditableTableSchema;
  mode: 'cell' | 'row';
  onCommit: (args: EditableCellCommitArgs<TData>) => EditableCellCommitResult;
  onRequestEditCell: (args: EditableCellEditRequestArgs<TData>) => void;
  getCanEditRow?: (row: Row<TData>) => boolean;
};

function renderReadOnlyCell<TData extends RowData, TValue>(
  column: ColumnDef<TData, TValue>,
  context: CellContext<TData, TValue>,
) {
  return column.cell ? flexRender(column.cell, context) : (context.getValue() as ReactNode);
}

export function wrapEditableColumns<TData extends RowData>({
  columns,
  schema,
  mode,
  onCommit,
  onRequestEditCell,
  getCanEditRow,
}: WrapEditableColumnsArgs<TData>): ColumnDef<TData, any>[] {
  const schemaShape = schema.shape;

  return columns.map((column) => {
    const editableMeta = column.meta?.editable;
    if (!editableMeta) {
      return column;
    }

    if (editableMeta.renderFromDraft) {
      if (mode !== 'row') {
        throw new Error('renderFromDraft editable columns are only supported in row mode');
      }
      const renderFromDraft = editableMeta.renderFromDraft;

      return {
        ...column,
        cell: (context) =>
          getCanEditRow?.(context.row) === false ? (
            renderReadOnlyCell(column, context)
          ) : (
            <RenderFromDraftCell<TData> context={context} renderFromDraft={renderFromDraft} />
          ),
      };
    }

    const columnId = getColumnDefId(column);
    if (!columnId) {
      throw new Error('Editable columns must define an id or accessorKey');
    }

    const fieldSchema = schemaShape[columnId];
    if (!fieldSchema) {
      throw new Error(`Editable column "${columnId}" must have a matching field in schema`);
    }

    const label = editableMeta.label ?? columnId;

    return {
      ...column,
      meta: {
        ...column.meta,
        cellClassName: (cell) =>
          cn(
            getCanEditRow?.(cell.row) !== false && [
              'relative overflow-hidden p-0 transition-colors',
              'focus-within:ring-1 focus-within:ring-primary focus-within:ring-inset hover:bg-muted/50 has-[[data-state=open]]:ring-1 has-[[data-state=open]]:ring-primary has-[[data-state=open]]:ring-inset',
            ],
            column.meta?.cellClassName?.(cell),
          ),
      },
      cell: (context) =>
        getCanEditRow?.(context.row) === false ? (
          renderReadOnlyCell(column, context)
        ) : (
          <EditableCellDisplay<TData>
            context={context}
            column={column}
            columnId={columnId}
            label={label}
            meta={editableMeta}
            fieldSchema={fieldSchema}
            onCommit={onCommit}
            onRequestEdit={onRequestEditCell}
          />
        ),
    };
  });
}
